import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ImageIcon from "@mui/icons-material/Image";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocalText from "../common/LocalText";

const TYPE_OPTIONS = [
  { key: "portal", label: "Portal", icon: OpenInNewIcon },
  { key: "media",  label: "Media",  icon: ImageIcon },
  { key: "custom", label: "Custom", icon: CalendarMonthIcon },
];

const SelectTypeModal = ({ open, onClose, onSelect, excludeKey, title = "Select Type" }) => {
  const [hovered, setHovered] = useState(null);
  const options = excludeKey ? TYPE_OPTIONS.filter((opt) => opt.key !== excludeKey) : TYPE_OPTIONS;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(10,50,109,0.22)",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          bgcolor: "primary.main",
          color: "white",
          py: 2.5,
          px: 3,
          pr: 6,
          lineHeight: 1,
        }}
      >
        <Typography variant="h6" fontWeight={700} component="div" lineHeight={1}>
          <LocalText>{title}</LocalText>
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            right: 12,
            top: "50%",
            transform: "translateY(-50%)",
            color: "white",
            "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Cards */}
      <DialogContent sx={{ px: { xs: 2, sm: 4 }, pt: 4, pb: 4, bgcolor: "white" }}>
        <Box sx={{ display: "flex", gap: { xs: 2, sm: 3 }, mt: 1 }}>
          {options.map((opt) => {
            const Icon = opt.icon;
            const isHovered = hovered === opt.key;
            return (
              <Box
                key={opt.key}
                onClick={() => onSelect(opt.key)}
                onMouseEnter={() => setHovered(opt.key)}
                onMouseLeave={() => setHovered(null)}
                sx={{
                  flex: 1,
                  border: "1.5px solid",
                  borderColor: isHovered ? "primary.main" : "grey.200",
                  borderRadius: 3,
                  py: { xs: 3, sm: 5 },
                  px: { xs: 1.5, sm: 3 },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2.5,
                  cursor: "pointer",
                  bgcolor: isHovered ? "rgba(10,50,109,0.03)" : "white",
                  transition: "border-color 0.15s ease, background-color 0.15s ease",
                  userSelect: "none",
                }}
              >
                <Box
                  sx={{
                    width: { xs: 60, sm: 80 },
                    height: { xs: 60, sm: 80 },
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: isHovered
                      ? "0 8px 24px rgba(10,50,109,0.35)"
                      : "0 4px 12px rgba(10,50,109,0.2)",
                    transform: isHovered ? "translateY(-3px)" : "translateY(0)",
                    transition: "transform 0.18s ease, box-shadow 0.18s ease",
                  }}
                >
                  <Icon sx={{ fontSize: { xs: 28, sm: 38 }, color: "white" }} />
                </Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{
                    color: "primary.main",
                    fontSize: "1.05rem",
                    letterSpacing: 0.2,
                  }}
                >
                  <LocalText>{opt.label}</LocalText>
                </Typography>
              </Box>
            );
          })}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SelectTypeModal;
